export const CHUNK_TRANSFER_MODE = {
  SINGLE_FILE: "SINGLE_FILE",
  CHUNKED: "CHUNKED",
};

export function planDeterministicChunks(fileSize, chunkSize) {
  const size = Number(fileSize || 0);
  const unit = Number(chunkSize || 0);
  if (size <= 0 || unit <= 0) {
    return [];
  }
  const chunks = [];
  for (let offset = 0, index = 0; offset < size; offset += unit, index += 1) {
    chunks.push({
      chunkIndex: index,
      byteOffset: offset,
      byteCount: Math.min(unit, size - offset),
    });
  }
  return chunks;
}

export function chunkBackoffDelay(attempt, {
  initialBackoffMs = 500,
  maxBackoffMs = 30000,
  jitter = Math.random,
} = {}) {
  const exponent = Math.max(0, Number(attempt || 1) - 1);
  const base = Math.min(maxBackoffMs, initialBackoffMs * Math.pow(2, exponent));
  return Math.min(maxBackoffMs, Math.round(base + (jitter() * base)));
}

export function isTransientChunkStatus(status) {
  return [408, 429, 500, 502, 503, 504].includes(Number(status));
}

export function isChunkedUploadEligible({
  uploadV2Active = false,
  uploadJobId = null,
  uploadJobLogId = null,
  fileSize = 0,
  minimumChunkedUploadSize = 0,
} = {}) {
  return Boolean(uploadV2Active)
    && Boolean(uploadJobId)
    && Boolean(uploadJobLogId)
    && Number(fileSize || 0) >= Number(minimumChunkedUploadSize || 0);
}

export class UploadSemaphore {
  constructor(limit) {
    this.limit = Math.max(1, Number(limit || 1));
    this.active = 0;
    this.waiting = [];
  }

  async acquire() {
    if (this.active < this.limit) {
      this.active += 1;
      return;
    }
    await new Promise((resolve) => this.waiting.push(resolve));
    this.active += 1;
  }

  release() {
    this.active = Math.max(0, this.active - 1);
    const next = this.waiting.shift();
    if (next) {
      next();
    }
  }
}

export async function withSemaphore(semaphore, fn) {
  await semaphore.acquire();
  try {
    return await fn();
  } finally {
    semaphore.release();
  }
}

export async function sleepForChunkRetry(delayMs, isCancelled = () => false) {
  if (isCancelled()) {
    return;
  }
  await new Promise((resolve) => setTimeout(resolve, Math.max(0, Number(delayMs || 0))));
}

export async function runLimitedChunkTasks(tasks, {
  perFileConcurrency = 1,
  globalSemaphore = null,
  isCancelled = () => false,
} = {}) {
  const pendingTasks = Array.isArray(tasks) ? tasks : [];
  if (!pendingTasks.length || isCancelled()) {
    return;
  }

  let nextTaskIndex = 0;
  const workerCount = Math.min(
    pendingTasks.length,
    Math.max(1, Number(perFileConcurrency || 1))
  );
  const runTask = async (task) => {
    if (globalSemaphore) {
      return await withSemaphore(globalSemaphore, async () => {
        if (isCancelled()) {
          return;
        }
        return await task();
      });
    }
    if (isCancelled()) {
      return;
    }
    return await task();
  };

  const workers = Array.from({ length: workerCount }, async () => {
    while (!isCancelled()) {
      const taskIndex = nextTaskIndex;
      nextTaskIndex += 1;
      if (taskIndex >= pendingTasks.length) {
        return;
      }
      await runTask(pendingTasks[taskIndex]);
    }
  });

  await Promise.all(workers);
}
